import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useApp} from "../../app.context";
import StringInput from "../../elements/stringInput.element";
import {useIntegrator} from "../../integrator.context";
import {ChangeLocalizationValuesCommand} from "../../commands/changeLocalizationValues.command";
import {RemoveLocalizationValueCommand} from "../../commands/removeLocalizationValue.command";
import {ChangeLocalizationValueCommand} from "../../commands/changeLocalizationValue.command";
import {PruneLocalizationKeysCommand} from "../../commands/pruneLocalizationKeys.command";
import {AddLocalizationKeysCommand} from "../../commands/addLocalizationKeys.command";
import {formatString} from "../../utils/text.utils";
import {
    codeLocalizationKeys,
    getUsedLocalizationKeys,
    missingCodeLocalization,
    staleCodeLocalizationKeys,
} from "../../utils/localization.utils";
import {LocalizationKeyManifestEntry} from "../../models/attributeManifest.model";

interface PruneDialogProps {
    orphanKeys: string[];
    // `loc.` keys the Client no longer names — flagged, since an older build may still ask.
    staleCodeKeys: Set<string>;
    onClose(): void;
}

// Modal listing every orphaned localization key (not referenced from data, not
// named by the Client per the attribute manifest) with a checkbox each, a search
// filter, and bulk delete.
function PruneDialog(props: PruneDialogProps) {
    const {state, executeCommand} = useIntegrator();
    const {confirm} = useApp();
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const shown = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (needle === '') return props.orphanKeys;
        return props.orphanKeys.filter(key =>
            key.toLowerCase().includes(needle) ||
            (state.localization['en']?.[key] ?? '').toLowerCase().includes(needle));
    }, [props.orphanKeys, search, state.localization]);

    function toggle(key: string) {
        const next = new Set(selected);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        setSelected(next);
    }

    function selectAllShown() {
        const next = new Set(selected);
        shown.forEach(key => next.add(key));
        setSelected(next);
    }

    async function deleteSelected() {
        // Guard against stale selections (keys pruned/renamed since selecting).
        const keys = props.orphanKeys.filter(key => selected.has(key));
        if (keys.length === 0) return;

        const proceed = await confirm({
            title: 'Prune Localization Keys',
            body: <p>Are you sure you want to delete <strong>{keys.length}</strong> key{keys.length === 1 ? '' : 's'}?</p>,
            confirmText: 'Delete',
            danger: true,
        });
        if (!proceed) return;

        executeCommand(new PruneLocalizationKeysCommand(keys));
        props.onClose();
    }

    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            {/* Fixed height (not max-height) so the dialog doesn't shrink and
                jump around as search filtering changes the row count. */}
            <div className={'layout vertical box'}
                 style={{padding: 15, gap: 10, width: 640, height: '85vh', overflow: 'hidden'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Prune Localization Keys</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
                    <input style={{flexGrow: 1, padding: 5}}
                           placeholder={'Search...'}
                           value={search}
                           autoFocus
                           onChange={e => setSearch(e.target.value)}/>
                    <button onClick={selectAllShown}>Select All Shown</button>
                </div>

                {props.staleCodeKeys.size > 0 && (
                    <p className={'muted-text'}>
                        Keys marked <strong>old client</strong> are ones this environment's Client no
                        longer asks for. A build still in players' hands may — prune them only once
                        every live build is newer.
                    </p>
                )}

                <div className={'layout vertical'} style={{gap: 4, overflowY: 'auto', flexGrow: 1}}>
                    {shown.map(key => (
                        <label key={key} className={'layout horizontal'}
                               style={{gap: 8, alignItems: 'center', cursor: 'pointer'}}>
                            <input type={'checkbox'}
                                   checked={selected.has(key)}
                                   onChange={() => toggle(key)}/>
                            <p style={{wordBreak: 'break-all', flexShrink: 0}}>{key}</p>
                            {props.staleCodeKeys.has(key) &&
                                <span className={'muted-text'} style={{flexShrink: 0, color: 'var(--tint-color)'}}>old client</span>}
                            <p className={'muted-text'}
                               style={{
                                   overflow: 'hidden', textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap', minWidth: 0,
                               }}>
                                {state.localization['en']?.[key]}
                            </p>
                        </label>
                    ))}
                    {shown.length === 0 && <p className={'muted-text'}>No orphaned keys{search ? ' match the search' : ''}.</p>}
                </div>

                <button disabled={selected.size === 0}
                        style={{opacity: selected.size > 0 ? 1 : 0.5}}
                        onClick={deleteSelected}>
                    Delete{selected.size > 0 ? ` (${selected.size})` : ''}
                </button>
            </div>
        </div>,
        document.body
    );
}

interface CodeKeysDialogProps {
    missing: LocalizationKeyManifestEntry[];
    onClose(): void;
}

// Modal listing the keys the Client asks for by name that this branch lacks, each with
// what it is for, its English and its placeholders, and an Add that seeds every
// language with that English.
function CodeKeysDialog(props: CodeKeysDialogProps) {
    const {executeCommand} = useIntegrator();

    function add(entries: LocalizationKeyManifestEntry[]) {
        if (entries.length === 0) return;
        executeCommand(new AddLocalizationKeysCommand(entries.map(entry => ({
            key: entry.Key,
            value: entry.English ?? entry.Key,
        }))));
    }

    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical box'}
                 style={{padding: 15, gap: 10, width: 760, height: '85vh', overflow: 'hidden'}}
                 onClick={e => e.stopPropagation()}>
                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Client Keys Missing From This Branch</p>
                    <button onClick={props.onClose}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                <p className={'muted-text'}>
                    The Client asks for these keys by name. In game a key this branch lacks shows
                    Master's text, and without that the Client's own English where it has one.
                    Adding a key seeds every language with the English below; keep its
                    {' {placeholders}'} when rewording.
                </p>

                <div className={'layout vertical'} style={{gap: 8, overflowY: 'auto', flexGrow: 1}}>
                    {props.missing.map(entry => (
                        <div key={entry.Key} className={'layout horizontal'} style={{gap: 10, alignItems: 'flex-start'}}>
                            <button onClick={() => add([entry])} title={'Add with this English'}>
                                <span className={'fa-solid fa-plus'}/>
                            </button>
                            <div className={'layout vertical'} style={{gap: 2, minWidth: 0}}>
                                <p style={{wordBreak: 'break-all'}}>{entry.Key}</p>
                                {entry.Description && <p className={'muted-text'}>{entry.Description}</p>}
                                <p>{entry.English ?? <span className={'muted-text'}>(no English declared)</span>}</p>
                                {entry.Placeholders.length > 0 &&
                                    <p className={'muted-text'}>Placeholders: {entry.Placeholders.map(p => `{${p}}`).join(' ')}</p>}
                                <p className={'muted-text'} style={{fontSize: '0.85em'}}>{entry.Sources.join(', ')}</p>
                            </div>
                        </div>
                    ))}
                    {props.missing.length === 0 && <p className={'muted-text'}>This branch has every key the Client names.</p>}
                </div>

                <button disabled={props.missing.length === 0}
                        style={{opacity: props.missing.length > 0 ? 1 : 0.5}}
                        onClick={() => add(props.missing)}>
                    Add All{props.missing.length > 0 ? ` (${props.missing.length})` : ''}
                </button>
            </div>
        </div>,
        document.body
    );
}

// Tooltip on a Client-owned key: what it is for and its placeholders.
function codeKeyTitle(entry: LocalizationKeyManifestEntry | undefined): string | undefined {
    if (!entry) return undefined;
    const lines = [`Client key${entry.Description ? `: ${entry.Description}` : ''}`];
    if (entry.Placeholders.length > 0)
        lines.push(`Placeholders: ${entry.Placeholders.map(p => `{${p}}`).join(' ')}`);
    if (entry.English) lines.push(`English: ${entry.English}`);
    return lines.join('\n');
}

// Column id for the key column (languages use their own code as the id).
const KEY_COL = '__key__';
const DEFAULT_KEY_WIDTH = 360;
const DEFAULT_LANG_WIDTH = 240;
const MIN_COL_WIDTH = 80;

function LocalizationEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {setNotification, localizationFocusKey, focusLocalizationKey, confirmDelete, attributeManifest} = useApp();

    const [newKey, setNewKey] = useState<string>('');
    const [showPrune, setShowPrune] = useState<boolean>(false);
    const [showCodeKeys, setShowCodeKeys] = useState<boolean>(false);
    // Per-column width + filter, keyed by column id (KEY_COL or a language code).
    const [colWidths, setColWidths] = useState<Record<string, number>>({});
    const [filters, setFilters] = useState<Record<string, string>>({});
    const focusRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (localizationFocusKey && focusRef.current) {
            focusRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [localizationFocusKey]);

    const languages = Object.keys(state.localization);
    const keys = Object.keys(state.localization[languages[0]]).sort((a, b) => a.localeCompare(b));
    const usedKeys = useMemo(() => getUsedLocalizationKeys(state, attributeManifest), [state, attributeManifest]);
    const codeKeys = useMemo(() => codeLocalizationKeys(attributeManifest), [attributeManifest]);
    const missingCodeKeys = useMemo(() => missingCodeLocalization(state, attributeManifest), [state, attributeManifest]);
    const staleCodeKeys = useMemo(() => new Set(staleCodeLocalizationKeys(state, attributeManifest)), [state, attributeManifest]);

    const widthOf = (id: string) =>
        colWidths[id] ?? (id === KEY_COL ? DEFAULT_KEY_WIDTH : DEFAULT_LANG_WIDTH);

    // Column widths are published as CSS custom properties on the scroll
    // container; header and row cells read them via var(). During a drag we
    // mutate the property imperatively (see below) so the ~hundreds of row
    // inputs re-layout via CSS inheritance WITHOUT any React re-render — only
    // the final width is committed to state on mouseup.
    const cssVar = (id: string) => `--col-${id === KEY_COL ? 'key' : id}`;
    const scrollRef = useRef<HTMLDivElement>(null);
    const colVars = useMemo(() => {
        const vars: Record<string, string> = {};
        vars[cssVar(KEY_COL)] = `${widthOf(KEY_COL)}px`;
        for (const l of languages) vars[cssVar(l)] = `${widthOf(l)}px`;
        return vars as React.CSSProperties;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colWidths, languages]);

    // Column resizing — drag the handle on a header cell's right edge.
    const drag = useRef<{id: string; startX: number; startW: number; lastW: number} | null>(null);
    useEffect(() => {
        function move(e: MouseEvent) {
            const d = drag.current;
            if (!d) return;
            d.lastW = Math.max(MIN_COL_WIDTH, d.startW + e.clientX - d.startX);
            // Imperative: no setState, so no row re-render mid-drag.
            scrollRef.current?.style.setProperty(cssVar(d.id), `${d.lastW}px`);
        }
        function up() {
            const d = drag.current;
            if (d) setColWidths(prev => ({...prev, [d.id]: d.lastW}));
            drag.current = null;
            document.body.style.cursor = '';
        }
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function startResize(e: React.MouseEvent, id: string) {
        e.preventDefault();
        const startW = widthOf(id);
        drag.current = {id, startX: e.clientX, startW, lastW: startW};
        document.body.style.cursor = 'col-resize';
    }

    // A row is shown only if every column's filter matches (AND).
    const visibleKeys = useMemo(() => {
        const keyFilter = (filters[KEY_COL] ?? '').trim().toLowerCase();
        const langFilters = languages
            .map(l => [l, (filters[l] ?? '').trim().toLowerCase()] as const)
            .filter(([, f]) => f !== '');
        return keys.filter(key => {
            if (keyFilter && !key.toLowerCase().includes(keyFilter)) return false;
            for (const [lang, f] of langFilters) {
                if (!(state.localization[lang][key] ?? '').toLowerCase().includes(f)) return false;
            }
            return true;
        });
    }, [keys, filters, languages, state.localization]);

    // NOTE: rendered as a function call ({headerCell(...)}), NOT as <HeaderCell/>.
    // A component defined inside this one gets a new identity every render, so
    // React would remount the filter input on each keystroke and lose focus.
    function headerCell(id: string, title: string) {
        const value = filters[id] ?? '';
        return (
            <div key={id} style={{width: `var(${cssVar(id)})`, flexShrink: 0, position: 'relative'}}>
                <p className={'text'} style={{
                    fontWeight: 600, padding: '0 2px 4px',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{title}</p>
                <div style={{position: 'relative'}}>
                    <input placeholder={'Filter...'}
                           value={value}
                           onChange={e => setFilters(prev => ({...prev, [id]: e.target.value}))}
                           // Global CSS gives inputs `transition: all`; restrict to
                           // `filter` so column-resize width changes aren't animated.
                           style={{width: '100%', boxSizing: 'border-box', padding: '5px 24px 5px 5px', transition: 'filter 0.2s ease'}}/>
                    {value !== '' && (
                        <button onClick={() => setFilters(prev => ({...prev, [id]: ''}))}
                                title={'Clear filter'}
                                style={{
                                    position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
                                    padding: 0, width: 18, height: 18, lineHeight: '18px',
                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                    color: 'var(--muted-text-color)',
                                }}>
                            <span className={'fa-solid fa-xmark'}/>
                        </button>
                    )}
                </div>
                {/* Right-edge resize handle */}
                <div onMouseDown={e => startResize(e, id)}
                     title={'Drag to resize'}
                     style={{
                         position: 'absolute', top: 0, right: -3, width: 6, height: '100%',
                         cursor: 'col-resize',
                     }}/>
            </div>
        );
    }

    function addNewKey() {
        if (newKey === '') {
            setNotification({
                title: 'Invalid Input',
                message: 'Please enter a valid key.'
            })
            return;
        }

        if (newKey in state.localization['en']) {
            setNotification({
                title: 'Key Already Exists',
                message: 'Please enter a new key.'
            })
            return;
        }

        executeCommand(new ChangeLocalizationValuesCommand(newKey, 'New Value'));
        // Reuse the "A"-button mechanism: scroll to + green-highlight the new row.
        focusLocalizationKey(newKey);
        setNewKey('');
    }

    return (
        <div className={'layout vertical'} style={{gap: 10, flexGrow: 1}}>
            <div ref={scrollRef} className={'layout vertical'}
                 style={{height: 0, overflow: 'auto', flexGrow: 1, ...colVars}}>
                {/* Sticky header: title + per-column filter, aligned to the rows
                    below via the same column widths and `gap` spacing. */}
                <div className={'layout horizontal gap'}
                     style={{
                         position: 'sticky', top: 0, zIndex: 1,
                         backgroundColor: 'var(--background-color)',
                         borderBottom: '1px solid var(--box-color)',
                         padding: '4px 0 8px', marginBottom: 4,
                     }}>
                    {headerCell(KEY_COL, 'Key')}
                    {languages.map(language => headerCell(language, language))}
                </div>

                <div className={'layout vertical gap'}>
                    {visibleKeys.map((key) => {
                        const isFocused = key === localizationFocusKey;
                        const isUnused = !usedKeys.has(key);
                        return (
                            <div key={key} className={'layout horizontal gap'}
                                 ref={isFocused ? focusRef : undefined}
                                 style={isFocused ? {
                                     backgroundColor: 'rgba(90, 154, 90, 0.3)',
                                     borderRadius: 4,
                                     padding: '2px 4px',
                                     transition: 'background-color 0.3s'
                                 } : undefined}
                            >
                                <div className={'layout horizontal'}
                                     style={{color: isUnused ? 'var(--tint-color)' : 'white', alignItems: 'center', gap: 10, width: `var(${cssVar(KEY_COL)})`, flexShrink: 0}}>
                                    <button onClick={async () => {
                                        if (await confirmDelete(formatString(key), 'localization key'))
                                            executeCommand(new RemoveLocalizationValueCommand(key));
                                    }}>
                                        <span className={'fa-solid fa-trash'}/>
                                    </button>
                                    <p title={codeKeyTitle(codeKeys?.get(key))}
                                       style={{
                                           padding: '5.75px 0px',
                                           fontSize: 'var(--font-size-default)',
                                           flex: 1,
                                           minWidth: 0,
                                           wordBreak: 'break-all',
                                       }}
                                    >
                                        {key}
                                    </p>
                                </div>
                                {languages.map((language) => (
                                    <StringInput key={language} value={state.localization[language][key]}
                                                 style={{width: `var(${cssVar(language)})`, boxSizing: 'border-box', flexShrink: 0, transition: 'filter 0.2s ease'}}
                                                 onChange={(v) =>
                                                     executeCommand(new ChangeLocalizationValueCommand(key, v, language))
                                                 }/>
                                ))}
                            </div>
                        )
                    })}
                    {visibleKeys.length === 0 &&
                        <p className={'muted-text'} style={{padding: 8}}>No keys match the filters.</p>}
                </div>
            </div>
            <div className={'layout horizontal'} style={{gap: 10}}>
                <button onClick={addNewKey}>Add New Key</button>
                <input style={{flexGrow: 1}} placeholder={'Enter key...'} value={newKey}
                       onChange={v => setNewKey(v.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter') addNewKey(); }}/>
                <button onClick={() => setShowCodeKeys(true)}
                        disabled={!codeKeys}
                        title={codeKeys ? 'Keys the Client asks for by name that this branch lacks'
                            : 'The attribute manifest predates Client key export — re-export it from Unity'}>
                    <span className={'fa-solid fa-code'} style={{marginRight: 6}}/>
                    Client Keys{missingCodeKeys.length > 0 ? ` (${missingCodeKeys.length} missing)` : ''}
                </button>
                <button onClick={() => setShowPrune(true)}>
                    <span className={'fa-solid fa-broom'} style={{marginRight: 6}}/>
                    Prune
                </button>
            </div>
            {showPrune && (
                <PruneDialog orphanKeys={keys.filter(key => !usedKeys.has(key))}
                             staleCodeKeys={staleCodeKeys}
                             onClose={() => setShowPrune(false)}/>
            )}
            {showCodeKeys && (
                <CodeKeysDialog missing={missingCodeKeys}
                                onClose={() => setShowCodeKeys(false)}/>
            )}
        </div>
    )
}

export default LocalizationEditorView;